import React from 'react';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

export type UnpaidCheckInChoice = 'checkin' | 'pay' | 'cancel';

/**
 * Prompt shown when staff check in an unpaid booking and payment is NOT enforced
 * (blockCheckInUntilPaid off). Offers three choices: take payment now, check in anyway, or cancel.
 * Resolves with the chosen action.
 *
 * `canTakePayment` is false while check-in is offline — taking payment needs the server, so the
 * option is hidden rather than offered and then failing.
 */
export async function confirmUnpaidCheckIn(canTakePayment: boolean = true): Promise<UnpaidCheckInChoice> {
	let choice: UnpaidCheckInChoice = 'cancel';
	try {
		await newConfirmModal(
			'Check in unpaid booking?',
			(onConfirm, onCancel) => (
				<>
					<NewModal.Header>
						<NewModal.Title>Check in unpaid booking?</NewModal.Title>
						<Button
							className="new-modal--close"
							icon={{ icon: 'close', iconPos: 'icon-left' }}
							display={Display.Text}
							colors={Colors.Alternate}
							onClick={onCancel}
							labelVisible={false}
						/>
					</NewModal.Header>
					<NewModal.Body>
						{canTakePayment
							? 'This booking has not been paid. You can take payment now, or check it in anyway.'
							: "This booking has not been paid, and payment can't be taken while you're offline. " +
								'You can check it in anyway and take payment once you are back online.'}
					</NewModal.Body>
					<NewModal.Footer>
						<Button
							display={canTakePayment ? Display.Outline : Display.Solid}
							colors={canTakePayment ? Colors.Grey : Colors.Primary}
							newStyles
							onClick={() => {
								choice = 'checkin';
								onConfirm();
							}}
						>
							Check in anyway
						</Button>
						{canTakePayment && (
							<Button
								display={Display.Solid}
								colors={Colors.Primary}
								newStyles
								onClick={() => {
									choice = 'pay';
									onConfirm();
								}}
							>
								Take payment now
							</Button>
						)}
					</NewModal.Footer>
				</>
			),
			{ resolveOnCancel: true },
		);
	} catch {
		return 'cancel';
	}
	return choice;
}
